/**
 * ClosedAreaView — pure presentation for the "Closed" area that trails the
 * stage lanes on a pipeline board (SE-5496; SE-6861 moved it ahead of the "Add
 * column" control, which now ends the strip; SE-6862 aligned it with Figma node
 * 80:18282).
 *
 * Two states, one box. Collapsed — the default — the area is a 44px RAIL: a
 * count badge over a quarter-turned summary of what is closed, taking almost no
 * board width. Expanded it becomes a 288px panel listing the closed cards
 * grouped by terminal outcome, in the board template's `validOutcomes` order
 * (the grouping is done upstream by `generateClosedGroups`).
 *
 * This file owns only the two states and the panel's header. Each group — its
 * marker, header, cards, and the "+N more" control that lifts its one-card cap
 * — is `OutcomeGroup`, and the collapsed rail is `CollapsedRail`; both are
 * separate components because a View file may hold exactly one.
 *
 * Desktop board chrome, matching the rest of the @dnd-kit board surface.
 * @module features/player-kanban/components/organisms/ClosedArea/ClosedAreaView
 */

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";
import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Divider } from "@/components/atoms/Divider";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import CollapsedRail from "./CollapsedRail";
import OutcomeGroup from "./OutcomeGroup";

import type { ClosedCardEntry, ClosedGroupData } from "../../../types/types";
import type { KanbanCardOutcome } from "@/generated/graphql";

/**
 * Lane chrome shared by both states (SE-6554).
 *
 * Character-for-character the lane chrome in `KanbanColumnView`, so Closed sits
 * in the same family as the stages it follows, in both themes
 * (`surface-raised` carries light AND dark; see the note in KanbanColumnView
 * about `dark:bg-black` flattening it). It replaced a borderless
 * `bg-surface-subtle` block that read as a raw grey slab parked beside the
 * board rather than as part of it.
 *
 * `self-start` is what keeps the area hugging its content height. The board row
 * is a flex row whose default `align-items: stretch` sizes every child to the
 * tallest one, so this box was being stretched into a floor-to-ceiling grey
 * column. It is deliberately NOT applied to the stage lanes: those are drop
 * targets and want the full column height to catch a dragged card anywhere down
 * the board.
 */
const LANE_CHROME_CLASS =
  "shrink-0 self-start rounded-lg border border-outline-default bg-surface-raised";

/** Props for {@link ClosedAreaView}. */
export interface ClosedAreaViewProps {
  /** Closed groups, pre-ordered by `validOutcomes`. */
  readonly closedGroups: readonly ClosedGroupData[];
  /**
   * Collapsed-rail summary label, derived from `closedGroups` by
   * `ClosedAreaContainer` (SE-8019). The View renders it; it does not
   * compute it.
   */
  readonly railLabel: string;
  /** Total closed-card count across all groups. */
  readonly totalCount: number;
  /** Whether the area is expanded. */
  readonly isExpanded: boolean;
  /** Toggle handler for the rail / collapse control. */
  readonly onToggle: () => void;
  /** Whether the viewer may reopen cards. */
  readonly canReopen: boolean;
  /** Reopen-request handler for a specific closed card. */
  readonly onReopenPress: (entry: ClosedCardEntry) => void;
  /** Outcomes whose group has been expanded past the cap. */
  readonly showAllOutcomes: ReadonlySet<KanbanCardOutcome>;
  /** Per-outcome "show the rest of this group" handlers. */
  readonly showMoreHandlers: Partial<Record<KanbanCardOutcome, () => void>>;
}

/**
 * Renders the Closed area in whichever state it is in.
 * @param props - {@link ClosedAreaViewProps}
 * @param props.closedGroups - Closed groups, pre-ordered
 * @param props.railLabel - Container-derived collapsed-rail summary label
 * @param props.totalCount - Total closed-card count
 * @param props.isExpanded - Whether the area is expanded
 * @param props.onToggle - Rail / collapse toggle handler
 * @param props.canReopen - Whether the viewer may reopen
 * @param props.onReopenPress - Reopen-request handler
 * @param props.showAllOutcomes - Outcomes expanded past the cap
 * @param props.showMoreHandlers - Per-outcome cap-lifting handlers
 * @returns The Closed area, or null when there are no closed cards.
 */
const ClosedAreaView = ({
  closedGroups,
  railLabel,
  totalCount,
  isExpanded,
  onToggle,
  canReopen,
  onReopenPress,
  showAllOutcomes,
  showMoreHandlers,
}: ClosedAreaViewProps) =>
  closedGroups.length === 0 ? null : !isExpanded ? (
    <Box
      testID={TEST_ID_PLAYER_KANBAN.CLOSED_AREA}
      className={`w-11 ${LANE_CHROME_CLASS}`}
    >
      <CollapsedRail
        railLabel={railLabel}
        totalCount={totalCount}
        onToggle={onToggle}
      />
    </Box>
  ) : (
    <Box
      testID={TEST_ID_PLAYER_KANBAN.CLOSED_AREA}
      className={`w-72 ${LANE_CHROME_CLASS}`}
    >
      <Stack className="w-full gap-2 px-3 py-3">
        {/* Header row: title, a rule filling the slack, and the collapse
            control. The row itself is NOT pressable — with cards below it, a
            whole-row target would swallow presses meant for the panel. */}
        <Stack direction="horizontal" className="w-full items-center gap-2">
          <Text variant="title-sm" className="text-content-primary">
            Closed
          </Text>
          <Divider className="flex-1 bg-outline-default" />
          <Pressable
            testID={TEST_ID_PLAYER_KANBAN.CLOSED_AREA_TOGGLE}
            onPress={onToggle}
            accessibilityRole="button"
            accessibilityLabel="Collapse closed"
          >
            <Text variant="micro" className="text-content-muted">
              Collapse ›
            </Text>
          </Pressable>
        </Stack>

        {closedGroups.map(group => (
          <OutcomeGroup
            key={group.outcome}
            group={group}
            isShowingAll={showAllOutcomes.has(group.outcome)}
            canReopen={canReopen}
            onReopenPress={onReopenPress}
            onShowMore={showMoreHandlers[group.outcome]}
          />
        ))}
      </Stack>
    </Box>
  );

ClosedAreaView.displayName = "ClosedAreaView";

export default memo(ClosedAreaView);
